import React from "react";
import { Combobox } from "../../ui/combobox";
import { CLOSED_CAMPAIGN_STATUSES } from "../../../utils/constants/CategoryTrackMapping";
import useMetaCampaigns from "../../../hooks/useMetaCampaigns";
import CampaignStatusTag from "../CampaignStatusTag";
import CampaignAccountHeader from "./CampaignAccountHeader";
import CampaignRotationRow from "./CampaignRotationRow";

/** One ad account's campaigns in rotation order: add at the end or remove. */
const CampaignAccountCard = ({ account, onChange, onRemove }) => {
  const {
    campaigns: metaCampaigns,
    isLoading,
    statusById,
  } = useMetaCampaigns({ adAccountId: account.meta_ad_account_id });
  const { campaigns } = account;
  const options = metaCampaigns
    .filter(
      (c) =>
        !CLOSED_CAMPAIGN_STATUSES.includes(String(c.status).toUpperCase()) &&
        !campaigns.some((x) => x.meta_campaign_id === c.id)
    )
    .map((c) => ({
      value: c.id,
      label: `${c.name} · ${c.id}`,
      tag: <CampaignStatusTag status={String(c.status).toUpperCase()} />,
    }));

  const handleAdd = (id) => {
    const found = metaCampaigns.find((c) => c.id === id);
    if (!found) return;
    onChange([
      ...campaigns,
      { meta_campaign_id: found.id, campaign_name: found.name },
    ]);
  };

  const handleRemove = ({ index }) =>
    onChange(campaigns.filter((_c, i) => i !== index));

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <CampaignAccountHeader
        account={account}
        count={campaigns.length}
        onRemove={onRemove}
      />
      {campaigns.length ? (
        <ol className="pt-1">
          {campaigns.map((c, index) => (
            <CampaignRotationRow
              key={c.meta_campaign_id}
              campaign={c}
              number={index + 1}
              status={statusById[c.meta_campaign_id]}
              isLast={index === campaigns.length - 1}
              onRemove={() => handleRemove({ index })}
            />
          ))}
        </ol>
      ) : (
        <p className="px-4 py-4 text-xs text-gray-500">
          No campaigns yet. Add the first one below.
        </p>
      )}
      <div className="border-t border-gray-100 px-4 py-3">
        <Combobox
          value=""
          options={options}
          onChange={handleAdd}
          disabled={isLoading}
          placeholder={
            isLoading ? "Loading campaigns…" : "+ Add campaign to the end"
          }
          searchPlaceholder="Search campaign name or ID…"
          emptyText="No more campaigns in this account."
          className="border-dashed text-gray-500 hover:border-[#7f7ad5]"
        />
      </div>
    </div>
  );
};

export default CampaignAccountCard;
